import React, { useEffect, useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { PlusIcon } from "lucide-react";
import { DataTableRowActions } from "./data-table-row-actions";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import EventForm from "../modal/form";
import { useGetEventsQuery } from "@/services/eventApi";
import TableFilters from "@/components/ui/table/data-table-filter";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import { Event } from "@/types/event";
import { displayValue } from "@/lib/helper";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { EventBookingType, EventStatus } from "@/types/enums";
import { Badge } from "@/components/ui/badge";
import { FacilitySchedule } from "@/types/facility/schedule";
import { Facility } from "@/types/facility";
import { useGetFacilitiesQuery, useGetFacilityScheduleByIdQuery } from "@/services/facilityApis";
import {
  addDays,
  addMonths,
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from "date-fns";
import {
  Calendar as FullCalendar,
  type CalendarEvent,
  type CalendarViewType,
} from "@/components/ui/full-calendar/calendar";
import { ViewToggle } from "@/components/ui/full-calendar/view-toggle";
import { FacilityScheduleSlot } from "@/types/facility/schedule";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePickerWithRange } from "@/components/ui/date-range/date-rangePicker";
import { formatDate } from "@/lib/helper";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { ScheduleType } from "@/types/facility/schedule";

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  search_key?: string;
  type: string;
  start_date?: string;
  end_date?: string;
  facility_ids?: string | (string | number)[];
  is_exclusive?: string;
}

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "events",
};

// Transform API slot data to calendar events
const transformScheduleSlotToEvent = (slot: FacilityScheduleSlot): CalendarEvent => {
  const dateStr = slot.slot_date || format(new Date(), "yyyy-MM-dd");
  const start = parseISO(`${dateStr}T${slot.start_time}`);
  const end = parseISO(`${dateStr}T${slot.end_time}`);

  const extractBaseColor = (colorString?: string): string | undefined => {
    if (!colorString) return undefined;
    const parts = colorString.split('-');
    return parts[0] || undefined;
  };

  // Format title: Event Name (Start Time - End Time)
  const timeRange = `${slot.start_time} - ${slot.end_time}`;
  const title = slot.name ? `${slot.name} (${timeRange})` : `${slot.facility_name} (${timeRange})`;

  return {
    id: slot.id.toString(),
    title,
    start,
    end,
    color: extractBaseColor(slot.color),
    description: slot.description,
    location: slot.location,
    originalData: slot,
    remaining_capacity: slot.remaining_capacity,
    is_exclusive: slot.is_exclusive,
  };
};

function EventsTableView() {
  const event = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).event ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const { searchCriteria, query, toggleSortOrder, setSearchCriteria, inputValue, setInputValue } =
    useTableData(initialValue);
  const [action, setAction] = useState<"add" | "edit">("add");
  const [data, setData] = useState<FacilitySchedule | undefined>(undefined);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  // search input
  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState<Record<string, any>>({});

  // Calendar View State
  const [viewMode, setViewMode] = useState<"calendar" | "grid">("grid");
  const [calendarViewType, setCalendarViewType] = useState<CalendarViewType>("month");
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  // Calendar date range state (separate from searchCriteria to handle navigation)
  const [calendarDateRange, setCalendarDateRange] = useState<{
    start_date: string;
    end_date: string;
  }>({
    start_date: format(startOfMonth(new Date()), "yyyy-MM-dd"),
    end_date: format(endOfMonth(new Date()), "yyyy-MM-dd"),
  });

  // Build unified query string based on view mode
  // For grid: use query from useTableData (includes type=events, pagination, filters)
  // For calendar: use date range + filters from searchCriteria (no type=events, no pagination)
  const unifiedQuery = React.useMemo(() => {
    if (viewMode === "grid") {
      // Table view: use query from useTableData + load_slots
      // query already includes type=events, pagination, and filters
      return query + "&load_slots=true&load_pricing=true";
    } else {
      // Calendar view: build query with date range + filters from searchCriteria
      const params = new URLSearchParams();
      params.append("load_slots", "true");
      params.append("type", "events");

      // Use date range from searchCriteria if available (from filter), otherwise use calendar navigation date range
      const criteria = searchCriteria as any;
      const startDate = criteria.start_date || calendarDateRange.start_date;
      const endDate = criteria.end_date || calendarDateRange.end_date;

      params.append("start_date", startDate);
      params.append("end_date", endDate);

      // Apply search key if present (trimmed)
      if (criteria.search_key !== undefined && criteria.search_key !== null && criteria.search_key.trim() !== "") {
        params.append("search_key", criteria.search_key.trim());
      }

      // Apply filters from searchCriteria (shared with table view)
      if (criteria.facility_ids !== undefined && criteria.facility_ids !== null) {
        if (Array.isArray(criteria.facility_ids)) {
          criteria.facility_ids.forEach((id: string | number) => {
            params.append("facility_ids", id.toString());
          });
        } else {
          params.append("facility_ids", criteria.facility_ids.toString());
        }
      }
      if (criteria.is_exclusive !== undefined && criteria.is_exclusive !== null && criteria.is_exclusive !== "") {
        params.append("is_exclusive", criteria.is_exclusive);
      }

      return params.toString();
    }
  }, [viewMode, query, calendarDateRange, searchCriteria]);

  // Single API call shared by both views
  const {
    data: eventData,
    isLoading,
    refetch,
  } = useGetEventsQuery(
    { org_id: orgId, query: unifiedQuery },
    {
      skip: !orgId || unifiedQuery === "",
    }
  );

  // Transform calendar events from slots array in the response
  const calendarEvents = React.useMemo(() => {
    if (viewMode !== "calendar" || !eventData?.data) return [];

    // Extract all slots from all events/schedules
    const allSlots: FacilityScheduleSlot[] = [];

    eventData.data.forEach((schedule: FacilitySchedule) => {
      if (schedule.slots && Array.isArray(schedule.slots)) {
        allSlots.push(...schedule.slots);
      }
    });

    return allSlots.map(transformScheduleSlotToEvent);
  }, [eventData, viewMode]);

  // Handle Calendar Navigation
  const handlePrevious = () => {
    setCurrentDate((prev) => {
      let newDate: Date;
      if (calendarViewType === "month") {
        newDate = subMonths(prev, 1);
      } else if (calendarViewType === "week") {
        newDate = addWeeks(prev, 1);
        newDate = subDays(prev, 7);
      } else {
        newDate = subDays(prev, 1);
      }
      return newDate;
    });
  };

  const handleNext = () => {
    setCurrentDate((prev) => {
      let newDate: Date;
      if (calendarViewType === "month") {
        newDate = addMonths(prev, 1);
      } else if (calendarViewType === "week") {
        newDate = addWeeks(prev, 1);
        newDate = addDays(prev, 7);
      } else {
        newDate = addDays(prev, 1);
      }
      return newDate;
    });
  };

  // Update calendar date range when date or view changes
  useEffect(() => {
    if (viewMode === "calendar") {
      let start: Date, end: Date;

      if (calendarViewType === "month") {
        start = startOfMonth(currentDate);
        end = endOfMonth(currentDate);
      } else if (calendarViewType === "week") {
        start = startOfWeek(currentDate, { weekStartsOn: 1 });
        end = endOfWeek(currentDate, { weekStartsOn: 1 });
      } else {
        start = startOfDay(currentDate);
        end = endOfDay(currentDate);
      }

      setCalendarDateRange({
        start_date: format(start, "yyyy-MM-dd"),
        end_date: format(end, "yyyy-MM-dd"),
      });
    }
  }, [currentDate, calendarViewType, viewMode]);

  // Handle Edit from Calendar
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(null);
  const { data: selectedScheduleData } = useGetFacilityScheduleByIdQuery(
    { org_id: orgId, schedule_id: selectedScheduleId!, query: "" },
    { skip: !selectedScheduleId }
  );

  React.useEffect(() => {
    if (selectedScheduleData) {
      setData(selectedScheduleData);
      setAction("edit");
      setIsDialogOpen(true);
      setSelectedScheduleId(null);
    }
  }, [selectedScheduleData]);

  // Event details dialog state
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isEventDetailsOpen, setIsEventDetailsOpen] = useState(false);

  // Handle event click from calendar - show event details
  const handleViewEvent = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setIsEventDetailsOpen(true);
  };

  const getDateRangeText = () => {
    if (calendarViewType === "month" || calendarViewType === "week") {
      const start = calendarViewType === "month" ? startOfMonth(currentDate) : startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = calendarViewType === "month" ? endOfMonth(currentDate) : endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(start, "dd-MM-yyyy")} to ${format(end, "dd-MM-yyyy")}`;
    } else {
      return format(currentDate, "dd-MM-yyyy");
    }
  };

  const handleDateRange = (dates: {
    start_date: Date | undefined;
    end_date: Date | undefined;
  }) => {
    const formattedStartDate = dates.start_date
      ? formatDate(dates.start_date)
      : "";
    const formattedEndDate = dates.end_date ? formatDate(dates.end_date) : "";

    setFilterData((prev) => ({
      ...prev,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    }));

    // Update search criteria (shared by both views)
    setSearchCriteria((prev: any) => ({
      ...prev,
      start_date: formattedStartDate ?? undefined,
      end_date:
        formattedStartDate && !formattedEndDate ? undefined : formattedEndDate,
      offset: 0,
    }));

    // Also update calendar date range if in calendar view
    if (viewMode === "calendar" && formattedStartDate && formattedEndDate) {
      setCalendarDateRange({
        start_date: formattedStartDate,
        end_date: formattedEndDate,
      });
    }
  };

  // Table data - only used in grid view
  const eventstableData = React.useMemo(() => {
    if (viewMode !== "grid" || !eventData?.data) return [];
    return Array.isArray(eventData.data) ? eventData.data : [];
  }, [eventData, viewMode]);

  const { data: facilitiesData } = useGetFacilitiesQuery(
    {
      query: "status=active&sort_key=id&sort_order=desc",
      orgId: orgId,
    },
    {
      skip: query == "",
    }
  );


  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const actionsColumn: ColumnDef<FacilitySchedule> = {
    accessorKey: "action",
    header: ({ table }) => <span>Action</span>,
    cell: ({ row }) => (
      <DataTableRowActions
        access={event}
        handleEdit={handleEdit}
        data={row.original}
        refetch={refetch}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  };

  const columns: ColumnDef<FacilitySchedule>[] = [
    {
      accessorKey: "name",
      meta: "Name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Event Name"
          sortKey="name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="flex items-center gap-2">

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="text-normal cursor-pointer">
                      <span>
                        {displayValue(
                          row.original.general_info.name && row.original.general_info.name.length > 20
                            ? row.original.general_info.name.substring(0, 20) + "..."
                            : row.original.general_info.name
                        )}
                      </span>
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-normal  text-sm">
                      {displayValue(row?.original?.general_info?.name)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },

    {
      accessorKey: "facility_name",
      meta: "Facility Name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Facility Name"
          sortKey="facility_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <span>{displayValue(row.original.general_info.facility_name) || "N/A"}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "tax_group",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Tax Group"
          sortKey="tax_group"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <span>{row.original.general_info.tax_group && displayValue(row.original.general_info.tax_group.name)}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "is_exclusive",
      meta: "Booking Type",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Booking Type"
          sortKey="is_exclusive"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const bookingType = row.original.general_info.is_exclusive ? "Fully Booked" : "Partially Booked";
        return (
          // <Badge
          //   variant={isFullyBooked ? "default" : "secondary"}
          //   className={isFullyBooked ? "bg-blue-500" : "bg-orange-500"}
          // >
          //   {isFullyBooked ? "Fully" : "Partial"}
          // </Badge>
          <p>{bookingType}</p>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "status",
      meta: "Status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const status = row.original.general_info.status;
        const getStatusBadge = () => {
          switch (status) {
            case EventStatus.Upcoming:
              return (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  Upcoming
                </Badge>
              );
            case EventStatus.Ongoing:
              return (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  Ongoing
                </Badge>
              );
            case EventStatus.Completed:
              return (
                <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
                  Completed
                </Badge>
              );
            default:
              return (
                <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
                  {status}
                </Badge>
              );
          }
        };
        // return getStatusBadge();
        return <p className="capitalize">{status}</p>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "max_attendees",
      meta: "Max Attendees",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Max Attendees"
          sortKey="max_attendees"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <span>{row.original.general_info.max_attendees}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "start_date",
      meta: "Start Date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Start Date"
          sortKey="start_date"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <span>{row.original.general_info.start_date}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "end_date",
      meta: "End Date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="End Date"
          sortKey="end_date"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <span>{row.original.general_info.end_date}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    ...(event !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: eventstableData as FacilitySchedule[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  const handleOpen = () => {
    setAction("add");
    setIsDialogOpen(true);
  };

  const handleEdit = (data: FacilitySchedule) => {
    setAction("edit");
    setData(data);
    setIsDialogOpen(true);
  };

  const totalRecords = eventData?.filtered_counts ?? 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleFilterChange = (
    field: string,
    value: string | React.ChangeEvent<HTMLInputElement> | (string | number)[]
  ) => {
    let newValue: string | (string | number)[] | undefined;

    if (Array.isArray(value)) {
      // Handle multiselect (array of values)
      newValue = value.length > 0 ? value : undefined;
    } else if (typeof value === "string") {
      // Handle string values
      newValue = value.trim() === "" ? undefined : value;
    } else {
      // Handle input events
      newValue = value.target.value.trim() === "" ? undefined : value.target.value;
    }

    setFilterData((prev) => {
      const newFilter = { ...prev };

      if (newValue === undefined || (Array.isArray(newValue) && newValue.length === 0)) {
        delete newFilter[field];
      } else {
        newFilter[field] = newValue;
      }

      return newFilter;
    });
  };

  const filterDisplay = [
    {
      type: "multiselect",
      name: "facility_ids",
      label: "Facility",
      options:
        facilitiesData?.data?.map((facility: Facility) => ({
          value: facility.id,
          label: facility.name,
        })) || [],
      function: (value: (string | number)[]) => handleFilterChange("facility_ids", value),
    },
    {
      type: "select",
      name: "is_exclusive",
      label: "Booking Type",
      options: [
        { id: "true", name: "Fully Booked" },
        { id: "false", name: "Partially Booked" },
      ],
      function: (value: string) => {
        // The select component passes the id as value
        handleFilterChange("is_exclusive", value);
      },
    },
  ];

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 px-3">
        {/* Search */}
        <TableSearchFilter setSearchCriteria={setSearchCriteria} inputValue={inputValue} setInputValue={setInputValue} />

        {/* Right group: calendar nav + action buttons — all in one row on lg+ */}
        <div className="flex flex-wrap items-center gap-2 shrink-0">

          {/* Calendar nav — only shown in calendar view, sits inline with buttons */}
          {viewMode === "calendar" && (
            <>
              <Button variant="ghost" size="icon" onClick={handlePrevious} className="h-8 w-8 shrink-0">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium min-w-[120px] lg:w-48 text-center">{getDateRangeText()}</span>
              <Button variant="ghost" size="icon" onClick={handleNext} className="h-8 w-8 shrink-0">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Select
                value={calendarViewType}
                onValueChange={(value) => setCalendarViewType(value as CalendarViewType)}
              >
                <SelectTrigger className="h-8 w-[80px] text-xs">
                  <SelectValue placeholder="View" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Month</SelectItem>
                  <SelectItem value="week">Week</SelectItem>
                  <SelectItem value="day">Day</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}

          {/* Grid-only controls */}
          {viewMode === "grid" && (
            <>
              <div className="text-sm text-black flex items-center gap-1 h-8">
                <DatePickerWithRange
                  name={"Date Range"}
                  value={{
                    start_date: filterData.start_date,
                    end_date: filterData.end_date,
                  }}
                  onValueChange={(dates) => {
                    handleDateRange(dates);
                  }}
                  label={"Select date range"}
                  className="w-full"
                />
              </div>
              <Button
                variant={"outline"}
                className="text-sm text-black flex items-center gap-1 h-8 px-2"
                onClick={() => {
                  setFilterData({});
                  setSearchCriteria(initialValue);
                  setInputValue("");
                }}
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="gap-2 flex justify-center items-center">
                        <i className="fa-solid fa-arrows-rotate"></i>
                        <span className="hidden sm:inline">Reset</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Reset Filter</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Button>
            </>
          )}

          {event !== "read" && (
            <Button
              className="bg-primary text-sm text-black flex items-center gap-1 h-8 px-2"
              onClick={handleOpen}
            >
              <PlusIcon className="size-4" />
              <span className="hidden sm:inline">Create New</span>
              <span className="sm:hidden">New</span>
            </Button>
          )}

          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>

          <ViewToggle
            view={viewMode}
            onViewChange={setViewMode}
            className="h-8"
          />
        </div>
      </div>
      {viewMode === "grid" ? (
        <>
          {/* ── Desktop table (md and up) ── */}
          <div className="hidden md:block rounded-none border border-border">
            <ScrollArea className="w-full relative" hideScrollToTop={true}>
              <ScrollBar
                orientation="horizontal"
                className="relative z-30 cursor-grab h-2"
              />
              <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]">
                <TableHeader className="bg-secondary">
                  {table?.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        return (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        <div className="flex space-x-2 justify-center items-center bg-white ">
                          <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                          <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                          <div className="size-3 bg-black rounded-full animate-bounce"></div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : table.getRowModel().rows.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && "selected"}
                        onClick={(e) => {
                          if (event !== "read" && (e.target instanceof Element &&
                            !e.target.closest('[role="checkbox"]') && (e.target === e.currentTarget || e.target.closest('td')))) {
                            handleEdit(row.original)
                          }
                        }}
                        className={event !== "read" ? "cursor-pointer hover:bg-secondary" : ""}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        No records found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>

          {/* ── Mobile card list (below md) ── */}
          <div className="block md:hidden px-3 space-y-3">
            {isLoading ? (
              <div className="flex space-x-2 justify-center items-center py-10">
                <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                <div className="size-3 bg-black rounded-full animate-bounce"></div>
              </div>
            ) : eventstableData.length === 0 ? (
              <p className="text-center text-sm text-gray-500 py-10">No records found.</p>
            ) : (
              eventstableData.map((item) => {
                const info = item.general_info;
                const bookingType = info.is_exclusive ? "Fully Booked" : "Partially Booked";
                return (
                  <div
                    key={info.id}
                    onClick={() => event !== "read" && handleEdit(item)}
                    className={`bg-white border border-border rounded-lg p-4 space-y-2 shadow-sm ${event !== "read" ? "cursor-pointer active:bg-secondary" : ""}`}
                  >
                    {/* Header row: name + actions */}
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold text-sm leading-tight break-all">
                        {info.name || "—"}
                      </p>
                      {event !== "read" && (
                        <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                          <DataTableRowActions
                            access={event}
                            handleEdit={handleEdit}
                            data={item}
                            refetch={refetch}
                          />
                        </div>
                      )}
                    </div>

                    {/* Detail rows */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                      <div>
                        <span className="font-medium text-gray-700">Facility: </span>
                        {info.facility_name || "N/A"}
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Tax Group: </span>
                        {info.tax_group?.name || "N/A"}
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Booking: </span>
                        {bookingType}
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Capacity: </span>
                        {info.max_attendees ?? "—"}
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Start: </span>
                        {info.start_date || "—"}
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">End: </span>
                        {info.end_date || "—"}
                      </div>
                      <div className="col-span-2">
                        <span className="font-medium text-gray-700">Status: </span>
                        <span className="capitalize">{info.status || "—"}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      ) : (
        <div className="h-screen">
          {isLoading ? (
            <div className="flex justify-center items-center h-full">
              <div className="flex space-x-2">
                <div className="size-4 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                <div className="size-4 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                <div className="size-4 bg-primary rounded-full animate-bounce"></div>
              </div>
            </div>
          ) : (
            <FullCalendar
              events={calendarEvents}
              initialView={calendarViewType}
              initialDate={currentDate}
              isCustomView={true}
              onViewEvent={handleViewEvent}
              onEditEvent={handleViewEvent}
              access={event}
              disableNavigation={true}
              hideEventActions={true}
            />
          )}
        </div>
      )}

      {/* pagination */}
      {eventstableData && eventstableData.length > 0 && viewMode == "grid" && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={(newCriteria: any) => {
          // Update searchCriteria (shared by both views)
          setSearchCriteria(newCriteria);

          // If date range is applied in calendar view, also update calendar date range
          if (viewMode === "calendar" && newCriteria.start_date && newCriteria.end_date) {
            setCalendarDateRange({
              start_date: newCriteria.start_date,
              end_date: newCriteria.end_date,
            });
          }
        }}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
        onReset={() => {
          // Reset calendar date range when filters are reset
          if (viewMode === "calendar") {
            const currentMonthStart = format(startOfMonth(currentDate), "yyyy-MM-dd");
            const currentMonthEnd = format(endOfMonth(currentDate), "yyyy-MM-dd");
            setCalendarDateRange({
              start_date: currentMonthStart,
              end_date: currentMonthEnd,
            });
          }
        }}
      />

      <EventForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        action={action}
        setAction={setAction}
        data={data}
        setData={setData}
        refetch={refetch}
      />

      {/* Event Details Dialog */}
      <Dialog open={isEventDetailsOpen} onOpenChange={setIsEventDetailsOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <span className="mr-2">Event Details</span>
            </DialogTitle>
          </DialogHeader>
          {selectedEvent && (() => {
            const slot = selectedEvent.originalData as FacilityScheduleSlot;
            const isEvent = slot.type === ScheduleType.Events;
            const isExclusive = slot.is_exclusive;
            const isFullyBooked = isEvent && isExclusive;
            return (
              <div className="space-y-4">
                <div className="grid gap-2">
                  <div className="flex items-start gap-2 justify-between">
                    <span className="font-medium text-nowrap whitespace-nowrap">Event Name:</span>
                    <p className="text-sm text-gray-600 break-all">
                      {slot.name || "N/A"}
                    </p>
                  </div>

                  <div className="flex items-start gap-2 justify-between">
                    <span className="font-medium text-nowrap whitespace-nowrap">Facility Name:</span>
                    <p className="text-sm text-gray-600 break-all">
                      {slot.facility_name || "N/A"}
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="font-medium">Time:</span>
                    <span className="text-sm text-gray-600">
                      {slot.start_time} - {slot.end_time}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="font-medium">Date:</span>
                    <span className="text-sm text-gray-600">
                      {slot.slot_date ? format(new Date(slot.slot_date), "EEEE, MMMM dd, yyyy") : "N/A"}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="font-medium">Type:</span>
                    <Badge
                      variant="outline"
                      className={
                        isEvent
                          ? "bg-blue-50 text-blue-700 border-blue-200"
                          : "bg-green-50 text-green-700 border-green-200"
                      }
                    >
                      {isEvent ? "Event" : "Schedule"}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="font-medium">Available Seats:</span>
                    <p className="text-sm text-gray-600">
                      {selectedEvent.remaining_capacity}
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="font-medium">Status:</span>
                    <Badge
                      variant="outline"
                      className="bg-red-50 text-red-700 border-red-200"
                    >
                      {isFullyBooked ? "Fully Booked" : "Exclusive"}
                    </Badge>
                  </div>



                  {slot.location && (
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Location:</span>
                      <p className="text-sm text-gray-600">{slot.location}</p>
                    </div>
                  )}

                  {slot.description && (
                    <div className="flex flex-col gap-1">
                      <span className="font-medium">Description:</span>
                      <p className="text-sm text-gray-600">{slot.description}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <DialogClose asChild>
              <Button>Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default EventsTableView;
